import { defineStore } from 'pinia';
import { MESSAGE_TYPE, MESSAGE_STATUS } from "../api/enums.js"
import useUserStore from './userStore.js';
import useFriendStore from './friendStore.js';
import useGroupStore from './groupStore.js';
import localForage from 'localforage';

/**
 * 优化1(冷热消息分区):
 * 热消息：登录后的消息
 * 冷消息: 登录前的消息
 * 每个会话的冷热消息分别用一个key进行存储，当有新的消息时，只更新热消息key，冷消息key保持不变
 * 由于热消息数量不会很大，所以localForage.setItem耗时很低，可以防止消息过多时出现卡顿的情况
 *
 * 优化2(延迟渲染):
 * 拉取消息时，如果直接用state.chats接收，页面就开始渲染，一边渲染页面一边大量接消息会导致很严重的卡顿
 * 为了加速拉取离线消息效率，拉取时消息暂时存储到cacheChats,等待所有离线消息拉取完成后，再统一放至state中进行渲染
 * 
 * 优化3(pinia代替vuex)
 * 实测pinia的远超vuex,且语法更简洁清晰
 * 
 * */


let cacheChats = [];
export default defineStore('chatStore', {
	state: () => {
		return {
			activeChat: null,
			chats: [],
			privateMsgMaxId: 0,
			groupMsgMaxId: 0,
			systemMsgMaxSeqNo: 0,
			/** 加载离线消息 */
			loading: false,

           /** ⭐ 新增写入锁（防止缓存被覆盖） */
			blockSave: true,  // loadChat 未完成之前不允许写入

			/** ⭐ 记录应该选中的会话 */
			pendingActiveChat: null,
		}
	},
	actions: {
		initChats(chatsData) {
			// 先记住当前选中的聊天，避免重新载入缓存时丢失目标。
			const activeInfo = this.pendingActiveChat || this.activeChat;
			if (activeInfo) {
				this.pendingActiveChat = {
					type: activeInfo.type,
					targetId: activeInfo.targetId
				};
			}

			this.chats = [];
			this.activeChat = null;

			this.privateMsgMaxId = chatsData.privateMsgMaxId || 0;
			this.groupMsgMaxId = chatsData.groupMsgMaxId || 0;
			this.systemMsgMaxSeqNo = chatsData.systemMsgMaxSeqNo || 0;

			cacheChats = chatsData.chats || [];

			// 如果目标会话已经在缓存里，立即绑定到缓存中的真实对象。
			// 后续 refreshChats() 会把同一批对象装入 this.chats。
			if (this.pendingActiveChat) {
				const pending = this.pendingActiveChat;
				const cachedActive = cacheChats.find(chat =>
					chat &&
					String(chat.type) === String(pending.type) &&
					String(chat.targetId) === String(pending.targetId)
				);
				if (cachedActive) {
					this.activeChat = cachedActive;
				}
			}
		},
		async openChat(chatInfo, isOpen=false) {
			 if (!chatInfo || !chatInfo.type || !chatInfo.targetId) {
				console.error("❌ openChat 参数不完整:", chatInfo);
				return null;
			}
			console.log("openChat..")

		  /** ⛔ 如果还在加载，则等待 loadChat 完成 */
            if (this.loading || this.blockSave) {
                console.log("⏳ openChat 等待 loadChat 完成...");
                await new Promise(resolve => {
                    const timer = setInterval(() => {
                        if (!this.loading && !this.blockSave) {
                            clearInterval(timer);
                            resolve();
                        }
                    }, 30);
                });
            }

            const chats = this.findChats();
            let chat = this.findChat(chatInfo);

			
            // 没有则创建新会话
            if (!chat) {
                chat = {
                    type: chatInfo.type,
                    targetId: chatInfo.targetId,
                    showName: chatInfo.showName || "",
                    headImage: chatInfo.headImage || "",
                    companyName: chatInfo.companyName || "",
                    unreadCount: 0,
                    messages: [],

                    atMe: false,
                    atAll: false,
                    lastAtMessageId: null,

                    stored: false,   // ⭐ 标记需要存储
                    delete: false,

                    lastContent: "",
                    lastSendTime: Date.now(),
                };

                chats.push(chat);
                console.log("🆕 创建新会话:", chat);
            }

            /**
             * 第三方固定客服/代理入口保护。
             *
             * 只限制 openChat() 的“自动激活”，不限制用户手动 setActiveChat()。
             * 因此不会影响 PC 普通聊天操作。
             */
            let fixedCustomerId = "";
            if (typeof sessionStorage !== "undefined") {
                fixedCustomerId = String(
                    sessionStorage.getItem("thirdFixedCustomerId") || ""
                );
            }

            const hasFixedCustomer =
                fixedCustomerId === "1" ||
                fixedCustomerId === "3" ||
                fixedCustomerId === "4";

            // const isExactFixedPrivateChat =
            //     chatInfo.type === "PRIVATE" &&
            //     String(chatInfo.targetId) === fixedCustomerId;

            // const allowAutoActive =
            //     !isOpen &&
            //     (
            //         !hasFixedCustomer ||
            //         isExactFixedPrivateChat
            //     );

            // if (allowAutoActive) {
            //     this.moveTop(chatInfo);
            //     this.setActiveChat(chat);  // ⭐ 立即激活
            // } else if (!isOpen && hasFixedCustomer) {
            //     console.log("[THIRD_CHAT][PC] auto active blocked =>", {
            //         fixedCustomerId,
            //         incomingType: chatInfo.type,
            //         incomingTargetId: chatInfo.targetId
            //     });
            // }

			const isExactFixedPrivateChat =
				chatInfo.type === "PRIVATE" &&
				String(chatInfo.targetId) === fixedCustomerId;


			/**
			 * ⭐ 真正应该选中的聊天
			 *
			 * 第三方模式如果有固定客服：
			 * 即使这里收到的是旧 chatInfo，
			 * 也优先选固定客服。
			 */
			let chatToActive = chat;

			if (hasFixedCustomer && !isExactFixedPrivateChat) {

				const fixedChat = this.findChatByFriend(fixedCustomerId);

				if (fixedChat) {

					chatToActive = fixedChat;

				} else {

					// 固定客服可能还没加载出来
					// 先记录下来，refreshChats 后恢复选中
					this.pendingActiveChat = {
						type: "PRIVATE",
						targetId: fixedCustomerId
					};
				}
			}


			const allowAutoActive =
				!isOpen &&
				(
					!hasFixedCustomer ||
					isExactFixedPrivateChat ||
					chatToActive !== chat
				);


			if (allowAutoActive) {

				this.moveTop(chatToActive);

				// ⭐ 真正设置当前聊天
				this.setActiveChat(chatToActive);

			} else if (!isOpen && hasFixedCustomer) {

				console.log("[THIRD_CHAT][PC] waiting fixed chat active =>", {
					fixedCustomerId,
					incomingType: chatInfo.type,
					incomingTargetId: chatInfo.targetId
				});
			}

            return chat;
        },
		setActiveChat(chatInfo) {
			if (!chatInfo) {
				this.activeChat = null;
				this.pendingActiveChat = null;
				return null;
			}

			// openChat() 现在是 async，旧调用点可能误把 Promise 传进来。
			// openChat 自己会负责自动激活，这里直接忽略 Promise，避免写入 undefined 目标。
			if (typeof chatInfo.then === 'function') {
				return null;
			}

			this.pendingActiveChat = {
				type: chatInfo.type,
				targetId: chatInfo.targetId
			};

			const sameChat = item =>
				item &&
				String(item.type) === String(chatInfo.type) &&
				String(item.targetId) === String(chatInfo.targetId);

			// 页面渲染依赖 this.chats，所以优先绑定这里的规范对象。
			let chat = this.chats.find(sameChat);

			// 页面列表尚未装载时，才从缓存区寻找。
			if (!chat && cacheChats) {
				chat = cacheChats.find(sameChat);
			}

			if (chat) {
				this.activeChat = chat;
			}

			return chat || null;
		},
		resetUnreadCount(chatInfo) {
			let chat = this.findChat(chatInfo);
            if (!chat) return;
			chat.unreadCount = 0;
			chat.stored = false;
			this.saveToStorage();
		},
		resetAtMessage(chatInfo) {
			  let chat = this.findChat(chatInfo);
            if (!chat) return;
			chat.atAll = false;
			chat.atMe = false;
			chat.lastAtMessageId = -1;
			chat.stored = false;
			this.saveToStorage();
		},
		readedMessage(pos) {
			let chat = this.findChatByFriend(pos.friendId);
			if (!chat) return;
			// 已读状态偶尔异常，目前找不到原因，暂时通过回溯更新1000条的方式解决
			let idx = Math.max(0, chat.readedMessageIdx - 1000);
			for (; idx < chat.messages.length; idx++) {
				let m = chat.messages[idx];
				if (m.id && m.selfSend && m.status < MESSAGE_STATUS.RECALL) {
					// pos.maxId为空表示整个会话已读
					if (!pos.maxId || m.id <= pos.maxId) {
						m.status = MESSAGE_STATUS.READED
						chat.readedMessageIdx = idx;
						chat.stored = false;
					}
				}
			}
			if (!chat.stored) {
				this.saveToStorage();
			}
		},
		cleanMessage(chatInfo) {
			 let chat = this.findChat(chatInfo);
            if (!chat) return;
			chat.lastContent = '';
			chat.hotMinIdx = 0;
			chat.unreadCount = 0;
			chat.atMe = false;
			chat.atAll = false;
			chat.lastAtMessageId = -1
			chat.stored = false;
			chat.messages = [];
			chat.readedMessageIdx = 0;
			this.saveToStorage(true);
		},
		removeChat(chatInfo) {
			  let chat = this.findChat(chatInfo);
            if (!chat) return;
			if (chat == this.activeChat) {
				this.activeChat = null;
			}
			chat.delete = true;
			chat.stored = false;
			this.saveToStorage()
		},
		removePrivateChat(friendId) {
			let chat = this.findChatByFriend(friendId);
			chat && this.removeChat(chat);
		},
		removeGroupChat(groupId) {
			let chat = this.findChatByGroup(groupId);
			chat && this.removeChat(chat);
		},
		moveTop(chatInfo) {
            if (this.loading || this.blockSave) return;

            let idx = this.findChatIdx(chatInfo);
            let chats = this.findChats();

            if (idx === undefined || idx === null || !chats[idx]) {
                return;
            }

            let chat = chats[idx];
            let insertIdx = chat.isTop ? 0 : this.topSize();

            if (idx !== insertIdx) {
                chats.splice(idx, 1);
                chats.splice(insertIdx, 0, chat);

                chat.optTime = Date.now();
                chat.stored = false;

                this.saveToStorage();
            }
        },
		async insertMessage(msgInfo, chatInfo) {
			   /** ⛔ 消息加载中禁止写入（防止覆盖缓存） */
            if (this.loading || this.blockSave) {
                console.log("⛔ insertMessage 阻止：聊天加载未完成");
                // 等待加载完成再执行
                await new Promise(resolve => {
                    const timer = setInterval(() => {
                        if (!this.loading && !this.blockSave) {
                            clearInterval(timer);
                            resolve();
                        }
                    }, 30);
                });
            }

            let type = chatInfo.type;

            // 记录最大 ID
            if (msgInfo.id && type === "PRIVATE" && msgInfo.id > this.privateMsgMaxId) {
                this.privateMsgMaxId = msgInfo.id;
            }
            if (msgInfo.id && type === "GROUP" && msgInfo.id > this.groupMsgMaxId) {
                this.groupMsgMaxId = msgInfo.id;
            }
            if (msgInfo.seqNo && type === "SYSTEM" && msgInfo.seqNo > this.systemMsgMaxSeqNo) {
                this.systemMsgMaxSeqNo = msgInfo.seqNo;
            }

            let chat = this.findChat(chatInfo);

            // 极端时序下消息可能比 openChat() 更早恢复执行。
            // 先保证会话存在，再插入消息，避免空对象或写进旧缓存对象。
            if (!chat) {
                await this.openChat(chatInfo, true);
                chat = this.findChat(chatInfo);
            }

            if (!chat) {
                console.error("❌ insertMessage 找不到会话:", chatInfo);
                return;
            }

            // 如果当前打开的就是这个会话，但 activeChat 还指向旧对象，
            // 立即重绑到真正接收消息的对象，确保右侧消息实时可见。
            if (
                this.activeChat &&
                String(this.activeChat.type) === String(chat.type) &&
                String(this.activeChat.targetId) === String(chat.targetId) &&
                this.activeChat !== chat
            ) {
                this.activeChat = chat;
            }

            let message = this.findMessage(chat, msgInfo);

            // 已存在 → 覆盖更新
            if (message) {
                Object.assign(message, msgInfo);
                chat.stored = false;

                this.saveToStorage();
                return;
            }

            /** 设置 lastContent */
            if (msgInfo.type === MESSAGE_TYPE.IMAGE) {
                chat.lastContent = "[图片]";
            } else if (msgInfo.type === MESSAGE_TYPE.VIDEO) {
                chat.lastContent = "[视频]";
            } else if (msgInfo.type === MESSAGE_TYPE.FILE) {
                chat.lastContent = "[文件]";
            } else if (msgInfo.type === MESSAGE_TYPE.AUDIO) {
                chat.lastContent = "[语音]";
            } else if (msgInfo.type === MESSAGE_TYPE.USER_CARD) {
                chat.lastContent = "[个人名片] " + JSON.parse(msgInfo.content).nickName;
            } else if (msgInfo.type === MESSAGE_TYPE.GROUP_CARD) {
                chat.lastContent = "[群名片] " + JSON.parse(msgInfo.content).groupName;
            } else if (msgInfo.type === MESSAGE_TYPE.ACT_RT_VOICE) {
                chat.lastContent = "[语音通话]";
            } else if (msgInfo.type === MESSAGE_TYPE.ACT_RT_VIDEO) {
                chat.lastContent = "[视频通话]";
            } else if (msgInfo.type === MESSAGE_TYPE.SYSTEM_MESSAGE) {
                chat.lastContent = msgInfo.title;
            } else {
                chat.lastContent = msgInfo.content;
            }

            chat.lastSendTime = msgInfo.sendTime;
            chat.sendNickName = msgInfo.sendNickName;
            chat.optTime = msgInfo.sendTime;

            /** 未读 +1 */
            if (
                !msgInfo.selfSend &&
                msgInfo.status !== MESSAGE_STATUS.READED &&
                msgInfo.status !== MESSAGE_STATUS.RECALL &&
                msgInfo.type !== MESSAGE_TYPE.TIP_TEXT
            ) {
                chat.unreadCount++;
            }

            /** 群 @ 提示 */
            if (
                !msgInfo.selfSend &&
                chat.type === "GROUP" &&
                msgInfo.atUserIds &&
                msgInfo.status !== MESSAGE_STATUS.READED
            ) {
                const userStore = useUserStore();
                let userId = userStore.userInfo.id;

                if (msgInfo.atUserIds.indexOf(userId) >= 0) {
                    chat.atMe = true;
                    chat.lastAtMessageId = msgInfo.id;
                }
                if (msgInfo.atUserIds.indexOf(-1) >= 0) {
                    chat.atAll = true;
                    chat.lastAtMessageId = msgInfo.id;
                }
            }

            /** 超 10 分钟插入时间提示 */
            if (!chat.lastTimeTip || chat.lastTimeTip < msgInfo.sendTime - 600000) {
                chat.messages.push({
                    sendTime: msgInfo.sendTime,
                    type: MESSAGE_TYPE.TIP_TIME,
                });
                chat.lastTimeTip = msgInfo.sendTime;
            }

            /** 插入真实消息 */
            chat.messages.push(msgInfo);
            chat.stored = false;

            this.saveToStorage();
            this.moveTop(chatInfo);
		},
		updateMessage(msgInfo, chatInfo) {
			// 获取对方id或群id
			let chat = this.findChat(chatInfo);
			let message = this.findMessage(chat, msgInfo);
			if (message) {
				// 属性拷贝
				Object.assign(message, msgInfo);
				chat.stored = false;
				this.saveToStorage();
			}
		},
		deleteMessage(msgInfo, chatInfo) {
			let isColdMessage = false;
			let chat = this.findChat(chatInfo);
			let delIdx = -1;
			for (let idx in chat.messages) {
				// 已经发送成功的，根据id删除
				if (chat.messages[idx].id && chat.messages[idx].id == msgInfo.id) {
					delIdx = idx;
					break;
				}
				// 正在发送中的消息可能没有id，只有临时id
				if (chat.messages[idx].tmpId && chat.messages[idx].tmpId == msgInfo.tmpId) {
					delIdx = idx;
					break;
				}
			}
			if (delIdx >= 0) {
				chat.messages.splice(delIdx, 1);
				if (delIdx < chat.hotMinIdx) {
					isColdMessage = true;
					chat.hotMinIdx--;
				}
				if (delIdx < chat.readedMessageIdx) {
					chat.readedMessageIdx--;
				}
				chat.stored = false;
				this.saveToStorage(isColdMessage);
			}
		},
		recallMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) return;
			let isColdMessage = false;
			// 要撤回的消息id
			let id = msgInfo.content;
			let name = msgInfo.selfSend ? '你' : chat.type == 'PRIVATE' ? '对方' : msgInfo.sendNickName;
			for (let idx in chat.messages) {
				let m = chat.messages[idx];
				if (m.id && m.id == id) {
					// 不是自己撤回，说明是被管理员撤回
					if (m.sendId != msgInfo.sendId) {
						name = "管理员"
					}
					// 改造成一条提示消息
					m.status = MESSAGE_STATUS.RECALL;
					m.content = name + "撤回了一条消息";
					m.type = MESSAGE_TYPE.TIP_TEXT
					// 会话列表
					chat.lastContent = m.content;
					chat.lastSendTime = msgInfo.sendTime;
					chat.sendNickName = '';
					if (!msgInfo.selfSend && msgInfo.status != MESSAGE_STATUS.READED) {
						chat.unreadCount++;
					}
					isColdMessage = idx < chat.hotMinIdx;
				}
				// 被引用的消息也要撤回
				if (m.quoteMessage && m.quoteMessage.id == id) {
					m.quoteMessage.content = "引用内容已撤回";
					m.quoteMessage.status = MESSAGE_STATUS.RECALL;
					m.quoteMessage.type = MESSAGE_TYPE.TIP_TEXT
				}
			}
			chat.stored = false;
			this.saveToStorage(isColdMessage);
		},
		updateChatFromFriend(friend) {
			let chat = this.findChatByFriend(friend.id);
			// 更新会话中的昵称和头像
			if (chat && (chat.headImage != friend.headImage ||
				chat.showName != friend.showNickName)) {
				chat.headImage = friend.headImage;
				chat.showName = friend.showNickName;
				chat.stored = false;
				this.saveToStorage();
			}
		},
		updateChatFromUser(user) {
			let chat = this.findChatByFriend(user.id);
			// 更新会话中的昵称和头像
			if (chat && (chat.headImage != user.headImageThumb ||
				chat.showName != user.nickName)) {
				chat.headImage = user.headImageThumb;
				chat.showName = user.nickName;
				chat.stored = false;
				this.saveToStorage();
			}
		},
		updateChatFromGroup(group) {
			let chat = this.findChatByGroup(group.id);
			if (chat && (chat.headImage != group.headImageThumb ||
				chat.showName != group.showGroupName)) {
				// 更新会话中的群名称和头像
				chat.headImage = group.headImageThumb;
				chat.showName = group.showGroupName;
				chat.stored = false;
				this.saveToStorage();
			}
		},
		setLoading(loading) {
			this.loading = loading;
		},
		setDnd(chatInfo, isDnd) {
			let chat = this.findChat(chatInfo);
			if (chat) {
				chat.isDnd = isDnd;
			}
		},
		setTop(chatInfo, isTop) {
			let chat = this.findChat(chatInfo);
			if (chat) {
				chat.isTop = isTop;
				this.moveTop(chatInfo);
			}
		},
		refreshChats() {
			let chats = cacheChats || this.chats;
			// 更新会话置顶和免打扰状态
			const friendStore = useFriendStore();
			const groupStore = useGroupStore();
			chats.forEach(chat => {
				if (chat.type == 'PRIVATE') {
					let friend = friendStore.findFriend(chat.targetId);
					if (friend) {
						chat.isDnd = friend.isDnd;
						chat.isTop = friend.isTop;
						chat.companyName = friend.companyName
					}
				} else if (chat.type == 'GROUP') {
					let group = groupStore.findGroup(chat.targetId);
					if (group) {
						chat.isDnd = group.isDnd;
						chat.isTop = group.isTop;
					}
				}
			})
			// 排序
			chats.sort((chat1, chat2) => {
				if (chat1.isTop && !chat2.isTop) {
					return -1;
				} else if (chat2.isTop && !chat1.isTop) {
					return 1;
				} else {
					return chat2.optTime - chat1.optTime
				}
			});
			/**
			 * 由于部分浏览器不支持websql或indexdb，只能使用localstorage，而localstorage大小只有10m,可能会导致缓存空间溢出
			 * 解决办法:针对只能使用localstorage的浏览器，最多保留1w条消息,每个会话最多保留1000条消息
			 */
			if (localForage.driver().includes("localStorage")) {
				this.fliterMessage(chats, 10000, 1000)
			}
			// 记录热数据索引位置
			chats.forEach(chat => {
				if (!chat.hotMinIdx || chat.hotMinIdx != chat.messages.length) {
					chat.hotMinIdx = chat.messages.length;
					chat.stored = false;
				}
			});
			// 将消息一次性装载回来。此后 this.chats 是唯一的页面会话来源。
			this.chats = chats;

			// 清空缓存，防止后续 findChat()/insertMessage() 再写到旧数组。
			cacheChats = null;

			const sameChat = (item, info) =>
				item && info &&
				String(item.type) === String(info.type) &&
				String(item.targetId) === String(info.targetId);

			// 恢复之前明确指定的会话，优先 pendingActiveChat。
			const activeInfo = this.pendingActiveChat || this.activeChat;
			let restoredActive = activeInfo
				? this.chats.find(item => sameChat(item, activeInfo))
				: null;

			// 第三方固定入口兜底：如果指定了 1 / 3 / 4，必须绑定对应 PRIVATE 会话。
			if (typeof sessionStorage !== "undefined") {
				const fixedCustomerId = String(
					sessionStorage.getItem("thirdFixedCustomerId") || ""
				);

				if (["1", "3", "4"].includes(fixedCustomerId)) {
					const fixedChat = this.chats.find(item =>
						item &&
						item.type === "PRIVATE" &&
						String(item.targetId) === fixedCustomerId
					);

					if (fixedChat) {
						restoredActive = fixedChat;
					}
				}
			}

			if (restoredActive) {
				this.activeChat = restoredActive;
				this.pendingActiveChat = {
					type: restoredActive.type,
					targetId: restoredActive.targetId
				};
			}

			// 持久化消息
			this.saveToStorage(true);
		},
		fliterMessage(chats, maxTotalSize, maxPerChatSize) {
			// 每个会话只保留maxPerChatSize条消息
			let remainTotalSize = 0;
			chats.forEach(chat => {
				if (chat.messages.length > maxPerChatSize) {
					let idx = chat.messages.length - maxPerChatSize;
					chat.messages = chat.messages.slice(idx);
				}
				remainTotalSize += chat.messages.length;
			})
			// 保证消息总数不超过maxTotalSize条，否则继续清理
			if (remainTotalSize > maxTotalSize) {
				this.fliterMessage(chats, maxTotalSize, maxPerChatSize / 2);
			}
		},
		saveToStorage(withColdMessage) {
			  /** ⛔ 加载中禁止写入（防止缓存被空会话覆盖） */
            if (this.loading || this.blockSave) {
                console.log("⛔ saveToStorage 阻止：加载未完成");
                return;
            }
			const userStore = useUserStore();
			let userId = userStore.userInfo.id;
			let key = "chats-" + userId;
			let chatKeys = [];
			const promises = [];
			// 按会话为单位存储
			for (let idx in this.chats) {
				let chat = this.chats[idx];
				// 只存储有改动的会话
				let chatKey = `${key}-${chat.type}-${chat.targetId}`
				if (!chat.stored) {
					if (chat.delete) {
						let hotKey = chatKey + '-hot';
						promises.push(localForage.removeItem(chatKey))
						promises.push(localForage.removeItem(hotKey))
					} else {
						// 存储冷数据
						if (withColdMessage) {
							let coldChat = Object.assign({}, chat);
							coldChat.messages = chat.messages.slice(0, chat.hotMinIdx);
							promises.push(localForage.setItem(chatKey, coldChat))
							console.log("coldKey:", chatKey)
						}
						// 存储热消息
						let hotKey = chatKey + '-hot';
						let hotChat = Object.assign({}, chat);
						hotChat.messages = chat.messages.slice(chat.hotMinIdx)
						promises.push(localForage.setItem(hotKey, hotChat))
						console.log("hotKey:", hotKey)
					}
					chat.stored = true;
				}
				if (!chat.delete) {
					chatKeys.push(chatKey);
				}
			}
			// 会话核心信息
			let chatsData = {
				privateMsgMaxId: this.privateMsgMaxId,
				groupMsgMaxId: this.groupMsgMaxId,
				systemMsgMaxSeqNo: this.systemMsgMaxSeqNo,
				chatKeys: chatKeys
			}
			Promise.all(promises).then(() => {
				localForage.setItem(key, chatsData)
			}).catch(() => {
				console.log("本地消息缓存存储失败")
			})
			// 清理已删除的会话
			this.chats = this.chats.filter(chat => !chat.delete)
		},
		clear() {
			cacheChats = null
			this.activeChat = null;
			this.chats = [];
			this.privateMsgMaxId = 0;
			this.groupMsgMaxId = 0;
			this.systemMsgMaxSeqNo = 0;
			this.loading = false;
			 this.blockSave = true;
    		this.pendingActiveChat = null;
		},
		 async loadChat() {
            this.loading = true;
            this.blockSave = true;  // ⭐ 启动写锁（防止第二窗口覆盖缓存）

            return new Promise((resolve, reject) => {
                const userStore = useUserStore();
                let userId = userStore.userInfo.id;
                let key = "chats-" + userId;

                localForage.getItem(key)
					.then(chatsData => {

						if (!chatsData || !chatsData.chatKeys) {

							// ⭐ 没缓存时必须清掉
							cacheChats = null;

							this.loading = false;
							this.blockSave = false;

							resolve();
							return;
						}

                        // 读取冷 + 热消息
                        const promises = [];
                        chatsData.chatKeys.forEach(k => {
                            promises.push(localForage.getItem(k));        // 冷
                            promises.push(localForage.getItem(k + "-hot")); // 热
                        });

                        Promise.all(promises).then(chats => {
                            chatsData.chats = [];

                            for (let i = 0; i < chats.length; i += 2) {
                                let coldChat = chats[i];
                                let hotChat = chats[i + 1];

                                if (!coldChat && !hotChat) continue;

                                /** 合并冷热消息 */
                                let chat = Object.assign({}, coldChat, hotChat);

                                if (coldChat && hotChat) {
                                    chat.messages = coldChat.messages.concat(hotChat.messages);
                                }

                                // 防止发送中卡住
                                hotChat &&
                                    hotChat.messages &&
                                    hotChat.messages.forEach(msg => {
                                        if (msg.status === MESSAGE_STATUS.SENDING) {
                                            msg.status = MESSAGE_STATUS.FAILED;
                                        }
                                    });

                                // 兼容缺失字段
                                chat.readedMessageIdx = chat.readedMessageIdx || 0;
                                chat.optTime = chat.optTime || chat.lastSendTime;

                                /** 若存在热消息，需要保存一次 */
                                chat.stored = !(hotChat && hotChat.messages.length);

                                chatsData.chats.push(chat);
                            }

                            this.initChats(chatsData);

                            this.loading = false;
                            this.blockSave = false; // ⭐ 解锁（现在才允许写入）

                            resolve();
                        });
                    })
                   .catch(err => {

					console.log("加载消息失败", err);

					// ⭐ 防止残留缓存影响 findChats
					cacheChats = null;

					this.loading = false;
					this.blockSave = false;

					reject();
				});
            });
        },
    },
	getters: {
		findChats: (state) => () => {
			// 加载/离线合并期间使用缓存区。
			if (state.loading && cacheChats) {
				return cacheChats;
			}

			// 一旦页面列表已经装载，始终以 state.chats 为规范数据源。
			if (state.chats && state.chats.length > 0) {
				return state.chats;
			}

			// loading=false 到 refreshChats() 之间可能有一个很短的窗口，
			// state.chats 仍为空，此时允许回退到缓存，避免瞬间找不到会话。
			if (cacheChats) {
				return cacheChats;
			}

			return state.chats;
		},
		findChatIdx: (state) => (chat) => {
			if (!chat) {
				return;
			}

			let chats = state.findChats();

			for (let idx in chats) {

				if (
					String(chats[idx].type) === String(chat.type) &&
					String(chats[idx].targetId) === String(chat.targetId)
				) {
					return idx;
				}
			}
		},
		findChat: (state) => (chat) => {
			let chats = state.findChats();
			let idx = state.findChatIdx(chat);
			return idx >= 0 ? chats[idx] : null;
		},
		findChatByFriend: (state) => (fid) => {
			let chats = state.findChats();
			return chats.find(chat => chat.type == 'PRIVATE' &&
				chat.targetId == fid)
		},
		findChatByGroup: (state) => (gid) => {
			let chats = state.findChats();
			return chats.find(chat => chat.type == 'GROUP' &&
				chat.targetId == gid)
		},
		findMessage: (state) => (chat, msgInfo) => {
			if (!chat) {
				return null;
			}
			if (chat.type == 'SYSTEM') {
				// 系统消息通过seqNo判断
				for (let idx in chat.messages) {
					if (msgInfo.seqNo && chat.messages[idx].seqNo == msgInfo.seqNo) {
						return chat.messages[idx];
					}
				}
			} else {
				// 通过id判断
				if (msgInfo.id) {
					for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
						let m = chat.messages[idx];
						if (m.id && msgInfo.id == m.id) {
							return m;
						}
						// 如果id比要查询的消息小，说明没有这条消息
						if (m.id && m.id < msgInfo.id) {
							break;
						}
					}
				}
				// 正在发送中的临时消息可能没有id,只有tmpId
				if (msgInfo.selfSend && msgInfo.tmpId) {
					for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
						let m = chat.messages[idx];
						if (!m.selfSend || !m.tmpId) {
							continue;
						}
						if (msgInfo.tmpId == m.tmpId) {
							return m;
						}
						// 如果id比要查询的消息小，说明没有这条消息
						if (m.tmpId && m.tmpId < msgInfo.tmpId) {
							break;
						}
					}
				}
			}
		},
		topSize: (state) => () => {
			let chats = state.findChats();
			return chats.filter(chat => chat.isTop).length;
		}
	}
});